// comments the code for phase 1

import {
  useCallback, useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import { apiGetRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";
import { mainContainerStyles } from "./styles/styles";
import BodyContent from "./Content/BodyContent";
import { DEFAULT_API_RECORDS_LIMIT } from "../../../Constants/index.js";
import { useDebounce } from "../../../hooks/useDebounce";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { formatRanges } from "../../../Helpers/formating";
import CommunitySuppliersHeaderFilters from "../../../Components/CommunitySuppliersHeaderFilters/index.js";
// import {
//   dummyCommunitiesData,
//   dummyMemberRanges,
//   dummyZonesData,
// } from "../DummyData/index.js";

// Utility function to shuffle an array
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function SearchCommunties() {
  const [locationDropDownList, setLocationDropDownList] = useState([]);
  const [rangesDropDownList, setRangesDropDownList] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedMemberRanges, setSelectedMemberRanges] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [communities, setCommunities] = useState([]);
  const [pendingJoinRequests, setPendingJoinRequests] = useState([]);
  const isReadyRef = useRef(false);
  const isFetched = useRef(false);
  const [maxPage, setMaxPage] = useState(1);
  const containerRef = useRef(null);
  const hasFilters =
    selectedLocations.length > 0 ||
    selectedMemberRanges.length > 0 ||
    searchValue.trim() !== "";

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [
        communitiesResult,
        zonesResult,
        rangesResult,
        pendingJoinRequestsResult,
      ] = await Promise.allSettled([
        apiGetRequest(
          endpoints.communities({
            page: 1,
            pageSize: DEFAULT_API_RECORDS_LIMIT,
          })
        ),
        apiGetRequest(endpoints.getZones),
        apiGetRequest(endpoints.getCommunityRanges),
        apiGetRequest(endpoints.getPendingJoinRequests),
      ]);

      if (communitiesResult.status === "fulfilled") {
        const shuffledCommunities = shuffleArray(
          communitiesResult.value.data?.communities || []
        );
        setCommunities(shuffledCommunities);
        setMaxPage(communitiesResult.value.data?.max_page_number ?? 1);
      } else {
        setCommunities([]);
      }

      setLocationDropDownList(
        zonesResult.status === "fulfilled" ? zonesResult.value.data : []
      );

      if (rangesResult.status === "fulfilled") {
        setRangesDropDownList(formatRanges(rangesResult.value.data));
      } else {
        setRangesDropDownList([]);
      }

      if (pendingJoinRequestsResult.status === "fulfilled") {
        setPendingJoinRequests(
          pendingJoinRequestsResult.value.data?.pending_community_ids || []
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        isReadyRef.current = true;
      }, 300);
    }
  }, []);

  const getMinMaxMembersRange = (ranges) => {
    let min = Infinity;
    let max = -Infinity;

    ranges.forEach((range) => {
      if (range.includes("+")) {
        let rangeMin = parseInt(range.replace(/,/g, "").replace("+", ""), 10);
        rangeMin += 1;

        min = Math.min(min, rangeMin);
        max = Math.max(max, rangeMin);
      } else if (range.includes("-")) {
        // Handle ranges with both min and max (e.g., "501-2000")
        const [rangeMin, rangeMax] = range
          .split("-")
          .map((val) => parseInt(val.replace(/,/g, ""), 10));

        // Update both min and max accordingly
        min = Math.min(min, rangeMin);
        max = Math.max(max, rangeMax);
      }
    });

    return {
      min: min === Infinity ? undefined : min,
      max: max === -Infinity ? undefined : max,
    };
  };

  const getCommunities = useCallback(
    async (page = activePage, isFilter = false) => {
      try {
        if (isFilter) {
          setIsFilterLoading(true);
        } else {
          setIsLoading(true);
        }

        const membersRange = selectedMemberRanges.length
          ? getMinMaxMembersRange(selectedMemberRanges)
          : null;
        const zoneIds = selectedLocations.length ? selectedLocations : [];

        const response = await apiGetRequest(
          endpoints.communities({
            page: page,
            pageSize: DEFAULT_API_RECORDS_LIMIT,
            searchValue,
            membersRange,
            zoneIds,
          })
        );

        // After a new search or more results, also fetch pending join requests to keep them in sync
        if (page === 1 || isFilter) {
          try {
            const pendingJoinRequestsResponse = await apiGetRequest(
              endpoints.getPendingJoinRequests
            );
            setPendingJoinRequests(
              pendingJoinRequestsResponse.data?.pending_community_ids || []
            );
          } catch (error) {
            console.error("Failed to refresh pending join requests:", error);
          }
        }

        setCommunities((prevCommunities) =>
          page === 1 || isFilter
            ? response?.data?.communities || []
            : [...prevCommunities, ...(response?.data?.communities || [])]
        );

        if (isFilter) {
          setMaxPage(response?.data?.max_page_number ?? 1);
          setActivePage(1);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (isFilter) {
          setIsFilterLoading(false);
          containerRef.current?.scrollTo({
            top: 0,
            behavior: "auto",
          });
        } else {
          setIsLoading(false);
        }
      }
    },
    [activePage, searchValue, selectedLocations, selectedMemberRanges]
  );

  const { debouncedValue: debouncedSearch } = useDebounce(searchValue, 1000);

  useLayoutEffect(() => {
    if (!isFetched.current) {
      fetchData();
      isFetched.current = true;
    }
  }, [fetchData]);

  const clearFilters = async () => {
    setSelectedLocations([]);
    setSelectedMemberRanges([]);
    setSearchValue("");
  };

  const handleScroll = () => {
    if (!containerRef.current || isLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 20 && activePage < maxPage) {
      setActivePage((prevPage) => {
        const nextPage = prevPage + 1;
        getCommunities(nextPage);

        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollTo({
              top: containerRef.current.scrollHeight,
              behavior: "smooth",
            });
          }
        }, 0);
        return nextPage;
      });
    }
  };

  useUpdateEffect(
    (changedDeps) => {
      if (isReadyRef.current) {
        const searchValueChange =
          changedDeps.length === 1 &&
          changedDeps.some((dep) => dep.name === "debouncedSearch");

        if (
          searchValueChange &&
          !(debouncedSearch.length >= 2 || debouncedSearch.length === 0)
        ) {
          console.log("Skipping API call: Search length is not valid");
          return;
        }
        // When any filter is added or removed, the page should always start from 1
        setActivePage(1);
        getCommunities(1, true);
      }
    },
    {
      debouncedSearch,
      selectedLocations,
      selectedMemberRanges,
    }
  );

  return (
    <Box sx={mainContainerStyles}>
      {/* header filters are disabled for phase 1 */}
      <Box sx={{ opacity: 1, pointerEvents: "auto" }}>
        <CommunitySuppliersHeaderFilters
          {...{
            searchValue,
            setSearchValue,
            clearFilters,
            selectedLocations,
            setSelectedLocations,
            zones: locationDropDownList,
            // zones: dummyZonesData,
            selectedMemberRanges,
            setSelectedMemberRanges,
            rangesDropDownList,
            // rangesDropDownList: dummyMemberRanges,
            hasFilters,
          }}
        />
      </Box>

      <BodyContent
        {...{
          isLoading,
          // isLoading: false,
          isFilterLoading,
          // isFilterLoading: false,
          communities,
          // communities: dummyCommunitiesData,
          containerRef,
          handleScroll,
          // handleScroll: () => {},
          debouncedSearch,
          pendingJoinRequests,
          // pendingJoinRequests: [],
        }}
      />
    </Box>
  );
}

export default SearchCommunties;
